import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FirebaseTSApp } from 'firebasets/firebasetsApp/firebaseTSApp';
import { environment } from 'src/environments/environment';
import { HomeComponent } from './pages/home/home.component';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { provideAuth, getAuth } from '@angular/fire/auth';

// Material Imports
import { MatButtonModule } from '@angular/material/button';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';

// Components
import { AuthenticatorComponent } from './tools/authenticator/authenticator.component';
import { EmailVerificationComponent } from './pages/email-verification/email-verification.component';
import { ProfileComponent } from './tools/profile/profile.component';
import { PostFeedComponent } from './pages/post-feed/post-feed.component';
import { CreatePostComponent } from './tools/create-post/create-post.component';
import { PostComponent } from './tools/post/post.component';
import { ReplyComponent } from './tools/reply/reply.component';
import { StoryComponent } from './story/story.component';
import { MessagesComponent } from './pages/messages/messages.component';
import { NotificationsComponent } from './pages/notifications/notifications.component';
import { FeedComponent } from './pages/feed/feed.component';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { EditPostDialogComponent } from './edit-post-dialog-component/edit-post-dialog-component.component';
import { CommentDialogComponent } from './comment-dialog/comment-dialog.component';
import { MonProfilComponent } from './pages/mon-profil/mon-profil.component';
import { EditProfileDialogComponent } from './edit-profile-dialog/edit-profile-dialog.component';
import { ChangeProfilePictureDialogComponent } from './change-profile-picture-dialog/change-profile-picture-dialog.component';
import { SearchComponent } from './pages/search/search.component';
import { ChatWindowComponent } from './tools/chat-window/chat-window.component';
import { VideoFeedComponent } from './pages/video-feed/video-feed.component';
import { PersonalInfoComponent } from './settings/personal-info/personal-info.component';
import { SettingsComponent } from './pages/settings/settings.component';
import { PrivacyComponent } from './settings/privacy/privacy.component';
import { LanguageComponent } from './settings/language/language.component';
import { ContentDisplayComponent } from './settings/content-display/content-display.component';
import { ProgressDialogComponent } from './progress-dialog/progress-dialog.component';
import { StoryViewerComponent } from './tools/story-viewer/story-viewer.component';
import { CreateStoryComponent } from './tools/create-story/create-story.component';
import { UserProfileComponent } from './tools/user-profile/user-profile.component';
import { AboutBottomSheetComponent } from './tools/user-profile/about-bottom-sheet/about-bottom-sheet.component';
import { UnfollowConfirmationDialogComponent } from './tools/user-profile/unfollow-confirmation-dialog/unfollow-confirmation-dialog.component';
import { EditProfileBottomSheetComponent } from './tools/edit-profile-bottom-sheet/edit-profile-bottom-sheet.component';
import { AboutComponent } from './settings/about/about.component';
import { AccountDeletionComponent } from './pages/account-deletion/account-deletion.component';
import { AccountDeletionService } from './services/account-deletion.service';
import { NiagaIaComponent } from './pages/niaga-ia/niaga-ia.component';

// Third Party Modules
import { LucideAngularModule } from 'lucide-angular';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { AngularFireStorageModule } from '@angular/fire/compat/storage';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

// Services and Guards
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { UnreadMessagesService } from './services/unread-messages.service';
import { GuestGuard } from './guards/guest.guard';
import { AuthService } from './services/auth.service';
import { MediaPlayerService } from './services/media-player.service';
import { MediaControlService } from './services/media-control.service';

// Pipes
import { FilterPipe } from "./filter.pipe";
import { MessageFilterPipe } from './pipes/message-filter.pipe';
import { HighlightTextPipe } from './pipes/highlight-text.pipe';
import { SortMessagesPipe } from './pipes/sort-messages.pipe';
import { FormatTextPipe } from './pipes/format-text.pipe';
import { ConfirmationDialogComponent } from './tools/confirmation-dialog/confirmation-dialog.component';
import { SafePipe } from './pipes/safe.pipe';

// Directives
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getStorage } from 'firebase/storage';
import { VirtualauthenticatorComponent } from './tools/virtualauthenticator/virtualauthenticator.component';
import { AdminModule } from './admin/admin.module';
import { FaqComponent } from './pages/faq/faq.component';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    AuthenticatorComponent,
    EmailVerificationComponent,
    ProfileComponent, 
    PostFeedComponent,
    CreatePostComponent,
    PostComponent,
    ReplyComponent,
    StoryComponent,
    MessagesComponent,
    NotificationsComponent,
    FeedComponent,
    EditPostDialogComponent,
    CommentDialogComponent,
    MonProfilComponent,
    EditProfileDialogComponent,
    ChangeProfilePictureDialogComponent,
    SearchComponent,
    ChatWindowComponent,
    VideoFeedComponent,
    PersonalInfoComponent,
    SettingsComponent,
    PrivacyComponent,
    LanguageComponent,
    ContentDisplayComponent,
    ProgressDialogComponent,
    FilterPipe,
    MessageFilterPipe,
    HighlightTextPipe,
    SortMessagesPipe,
    FormatTextPipe,
    ConfirmationDialogComponent,
    StoryViewerComponent,
    CreateStoryComponent,
    UserProfileComponent,
    AboutBottomSheetComponent,
    UnfollowConfirmationDialogComponent,
    SafePipe,
    ClickOutsideDirective,
    VirtualauthenticatorComponent,
    AboutComponent,
    FaqComponent,
    AccountDeletionComponent,
    NiagaIaComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    EditProfileBottomSheetComponent,
    
    // Angular Material Modules
    MatButtonModule,
    MatBottomSheetModule,
    MatCardModule,
    MatDialogModule,
    MatIconModule,
    MatBadgeModule,
    MatToolbarModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatSnackBarModule,
    MatSelectModule,
    MatSidenavModule,
    MatListModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatCheckboxModule,
   
    // Third Party Modules
    LucideAngularModule,
    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFireAuthModule,
    AngularFirestoreModule,
    AngularFireStorageModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    AdminModule
  ],
  providers: [
    MediaControlService,
    MediaPlayerService,
    UnreadMessagesService,
    GuestGuard,
    AuthService,
    AccountDeletionService,
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy
    },
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() {
    FirebaseTSApp.init(environment.firebaseConfig);
  }
}
